import {
  Categorical,
  Histogram,
} from '@cotera/client/app/components/data-vis/spark';
import { Loading, Text } from '@cotera/client/app/components/ui';
import {
  useDuckDBQuery,
  useSummariseQuery,
} from '@cotera/client/app/etc/data/duckdb';
import { METADATA_CONFIG } from '@cotera/client/app/etc/duckdb/constants';
import {
  Constant,
  Relation,
  TC,
  Histogram as HistogramRel,
  Case,
} from '@cotera/era';
import { CategoricalAttributes } from '@cotera/sdk/core';
import { DataGridModel } from './model';
import {
  Suspense,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import React from 'react';
import { DefaultHeader } from '../components/data-grid/header-renderer';
import { HeaderComponentProps } from '../components/data-grid';
import { DropdownMenuItem } from '../components/ui/dropdown-menu';
import { useAction, useWatchableValue, Watchable } from '../actions/framework';
import { cn } from '../lib/utils';
import { Assert } from '@cotera/utilities';
import { useAnalytics } from '@cotera/client/app/etc';
import { toggleCategoryFilter, toggleContinuousFilter } from './utils';
import { OpenEditColumnAction } from '../dataset/actions/open-edit-column.action';
import { Button } from '../components/ui/button';

export const LoadingStats = () => (
  <div className="h-[45px] flex flex-col pb-4">
    <Loading.Shimmer className="h-[25px] w-full mb-2" />
    <Loading.Shimmer className="h-[8px] w-1/2 " />
  </div>
);

const FilterableCategorical: React.FC<{
  rel: Relation;
  name: string;
  model: DataGridModel;
  summaryData: {
    approx_unique: number;
  } | null;
  data: {
    count: number;
    value: string | null;
  }[];
}> = ({ summaryData, data, model, name }) => {
  const filters = useWatchableValue(model.filters);
  const analytics = useAnalytics();

  const filterForColumn = filters.find(
    (f) => f.column === name && f.filter.t === 'category'
  ) ?? {
    column: name,
    filter: {
      t: 'category',
      value: [],
    },
  };

  Assert.assert(filterForColumn.filter.t === 'category');

  return (
    <Categorical
      onClick={(category) => {
        const currentFilters = model.filters.snapshot();
        const wasFiltered = currentFilters.some(
          (f) =>
            f.column === name &&
            f.filter.t === 'category' &&
            f.filter.value.includes(category)
        );

        analytics.track('Column Filtered', {
          column: name,
          filterType: 'categorical',
          value: category,
          action: wasFiltered ? 'removed' : 'added',
        });

        model.filters.set(toggleCategoryFilter(currentFilters, name, category));
      }}
      categories={data}
      active={filterForColumn.filter.value}
      numDistinct={Number(summaryData?.['approx_unique'] ?? 0)}
    />
  );
};

const CategoricalHeader: React.FC<{
  rel: Relation;
  name: string;
  model: DataGridModel;
}> = ({ rel, name, model }) => {
  const { data: result } = useDuckDBQuery({
    rel: CategoricalAttributes(rel, (r) => r.attr(name), {
      discreteThreshold: METADATA_CONFIG.MAX_CATEGORIES,
    }),
  });

  const { data: summaryRecords } = useSummariseQuery({
    rel: (rel) => rel.select((t) => ({ [name]: t.attr(name) })),
    baseRel: rel,
  });

  const summaryData = summaryRecords.data.toArray().at(0);

  return (
    <FilterableCategorical
      rel={rel}
      name={name}
      model={model}
      summaryData={summaryData as { approx_unique: number } | null}
      data={result.data.toArray() as { count: number; value: string | null }[]}
    />
  );
};

export const ContinuousHeader: React.FC<{
  rel: Relation;
  name: string;
  model: DataGridModel;
}> = ({ rel, name, model }) => {
  const { data: result } = useDuckDBQuery({
    rel: HistogramRel(
      rel,
      (r) => ({ target: r.attr(name), group: Constant('all') }),
      { numBuckets: 10 }
    ),
  });

  return (
    <div className="w-full h-[45px]">
      <FilterableContinuous
        data={
          result.data.toArray() as {
            count: number | null;
            start: number | null;
            end: number | null;
          }[]
        }
        name={name}
        model={model}
      />
    </div>
  );
};

const FilterableContinuous: React.FC<{
  name: string;
  model: DataGridModel;
  data: {
    count: number | null;
    start: number | null;
    end: number | null;
  }[];
}> = ({ name, model, data }) => {
  const filters = useWatchableValue(model.filters);
  const analytics = useAnalytics();

  return (
    <Histogram
      activeBuckets={
        (filters.find((f) => f.column === name && f.filter.t === 'continuous')
          ?.filter.value ?? []) as { min: number; max: number }[]
      }
      onClick={(min, max) => {
        analytics.track('Column Filtered', {
          column: name,
          filterType: 'continuous',
          value: `${min}-${max}`,
          action: 'applied',
        });

        model.filters.set(
          toggleContinuousFilter(model.filters.snapshot(), name, { min, max })
        );
      }}
      buckets={data}
    />
  );
};

const defaultSort = Watchable.fromValue<
  {
    column: string;
    direction: 'asc' | 'desc';
  }[]
>([]);

const SortableHeader: React.FC<
  HeaderComponentProps & {
    model: DataGridModel;
    actions?: React.ReactNode;
    title: React.ReactNode;
  }
> = (props) => {
  const openEdit = useAction(OpenEditColumnAction);
  const { model } = props;

  const sort = useWatchableValue(model?.sort ?? defaultSort);
  const analytics = useAnalytics();
  const bg = sort.some((x) => x.column === props.column.key)
    ? 'bg-yellow-100'
    : 'bg-white';

  const activeSort = sort.find((x) => x.column === props.column.key)?.direction;

  const handleSort = (direction: 'asc' | 'desc') => {
    if (activeSort === direction) {
      // Removing existing sort
      analytics.track('Column Sorted', {
        column: props.column.key,
        direction: 'none',
        action: 'removed',
      });
      model.sort.set(
        sort.filter((x) => !(x.column === props.column.key && x.direction))
      );
      return;
    }

    // Adding new sort
    analytics.track('Column Sorted', {
      column: props.column.key,
      direction,
      action: 'applied',
    });
    model.sort.set([
      ...sort.filter((x) => x.column !== props.column.key),
      {
        column: props.column.key,
        direction,
      },
    ]);
  };

  return (
    <DefaultHeader
      {...props}
      className={cn(props.className, bg)}
      title={props.column.key}
      onClick={async () => {
        if (props.column.t !== 'assumption') {
          await openEdit.execute({ name: props.column.key });
        }
      }}
      actions={
        <>
          <DropdownMenuItem asChild>
            <Button
              variant={activeSort === 'desc' ? 'active' : 'ghost'}
              icon="sort-desc"
              className="w-full justify-start"
              onClick={() => {
                handleSort('desc');
              }}
            >
              Sort Desc
            </Button>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Button
              variant={activeSort === 'asc' ? 'active' : 'ghost'}
              icon="sort-asc"
              className="w-full justify-start"
              onClick={() => {
                handleSort('asc');
              }}
            >
              Sort Asc
            </Button>
          </DropdownMenuItem>
          {props.column.status !== 'hidden' && (
            <DropdownMenuItem asChild>
              <Button
                variant={'ghost'}
                icon="eye-slash"
                className="w-full justify-start"
                onClick={() => {
                  model.columns.set(
                    model.columns
                      .snapshot()
                      .map((c) =>
                        c.key === props.column.key
                          ? { ...c, status: 'hidden' }
                          : c
                      )
                  );
                }}
              >
                Hide
              </Button>
            </DropdownMenuItem>
          )}
          {props.actions}
        </>
      }
    />
  );
};

// Hook that returns true once the input value has ever been true
const useHasValueBeenTrue = (value: boolean): boolean => {
  const [hasBeenTrue, setHasBeenTrue] = useState(value);

  useEffect(() => {
    if (value && !hasBeenTrue) {
      setHasBeenTrue(true);
    }
  }, [value, hasBeenTrue]);

  return hasBeenTrue;
};

const VisibleStats: React.FC<{
  children: React.ReactNode;
  model: DataGridModel;
  column: {
    key: string;
  };
}> = ({ children, model, column }) => {
  const columns = useWatchableValue(model.columns);
  const isVisible = columns.some((c) => c.key === column.key && c.inView);
  const hasColumnBeenVisible = useHasValueBeenTrue(isVisible);

  const memoizedChildren = useMemo(() => children, [children]);

  if (!hasColumnBeenVisible) {
    return (
      <div
        className="h-full w-full"
        onMouseOver={() => {
          model.columns.set(
            columns.map((c) =>
              c.key === column.key ? { ...c, inView: true } : c
            )
          );
        }}
      />
    );
  }

  return memoizedChildren;
};

const HeaderStats: React.FC<{
  children: React.ReactNode;
  model: DataGridModel;
  column: {
    key: string;
  };
}> = ({ children, model, column }) => {
  const headersHaveBeenExpanded = useWatchableValue(
    model.headersHaveBeenExpanded
  );
  const headersExpanded = useWatchableValue(model.headersExpanded);

  if (!headersHaveBeenExpanded) {
    return null;
  }

  return (
    <ErrorBoundary
      fallback={
        <Text.Caption className="text-destructive">
          Error loading stats
        </Text.Caption>
      }
    >
      <div
        className={cn('w-full h-full', headersExpanded ? 'visible' : 'hidden')}
      >
        <Suspense fallback={<LoadingStats />}>
          <VisibleStats model={model} column={column}>
            {children}
          </VisibleStats>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
};

const BaseHeaderRenderer: React.FC<
  HeaderComponentProps & {
    artifact: Relation;
    model: DataGridModel;
  }
> = (props) => {
  //this lowers the rendering priority of the header
  const deferredName = useDeferredValue(props.column.key);

  const column = props.artifact.attributes[props.column.key];

  const openEdit = useAction(OpenEditColumnAction);

  const actions = (
    <>
      {props.column.t !== 'assumption' && (
        <DropdownMenuItem asChild>
          <Button
            variant={'ghost'}
            className="w-full justify-start"
            icon="edit"
            onClick={async () => {
              await openEdit.execute({ name: props.column.key });
            }}
          >
            Edit
          </Button>
        </DropdownMenuItem>
      )}
    </>
  );

  const title = (
    <span
      className={cn(
        props.column.t !== 'assumption' && 'hover:text-primary cursor-pointer'
      )}
      onClick={async () => {
        if (props.column.t !== 'assumption') {
          await openEdit.execute({ name: props.column.key });
        }
      }}
    >
      {props.column.title}
    </span>
  );

  if (column === undefined) {
    return <SortableHeader {...props} title={title} />;
  }

  if (TC.isStringLike(column) || TC.isBoolean(column)) {
    const filterForColumn = props.model.filters
      .snapshot()
      .find((f) => f.column === deferredName && f.filter.t === 'category') ?? {
      column: deferredName,
      filter: {
        t: 'category',
        value: [],
      },
    };

    Assert.assert(filterForColumn.filter.t === 'category');

    return (
      <SortableHeader {...props} actions={actions} title={title}>
        <HeaderStats
          model={props.model}
          column={{
            key: deferredName,
          }}
        >
          <CategoricalHeader
            rel={props.artifact}
            name={deferredName}
            model={props.model}
          />
        </HeaderStats>
      </SortableHeader>
    );
  }

  if (TC.isNumeric(column)) {
    return (
      <SortableHeader {...props} actions={actions} title={title}>
        <HeaderStats
          model={props.model}
          column={{
            key: deferredName,
          }}
        >
          <ContinuousHeader
            rel={props.artifact}
            name={deferredName}
            model={props.model}
          />
        </HeaderStats>
      </SortableHeader>
    );
  }

  if (TC.isTimestamp(column)) {
    return (
      <SortableHeader
        {...props}
        actions={actions}
        title={title}
        className="h-full"
      />
    );
  }

  if (TC.isStruct(column)) {
    return (
      <DefaultHeader
        {...props}
        actions={actions}
        title={title}
        className="bg-white"
      >
        <HeaderStats
          model={props.model}
          column={{
            key: deferredName,
          }}
        >
          <CategoricalHeader
            rel={props.artifact.select((t) => ({
              [deferredName]: Case(
                [
                  {
                    when: t.attr(deferredName).isNotNull(),
                    then: 'exists',
                  },
                ],
                {
                  else: Constant(null, { ty: 'string' }),
                }
              ),
            }))}
            name={deferredName}
            model={props.model}
          />
        </HeaderStats>
      </DefaultHeader>
    );
  }

  return (
    <DefaultHeader
      {...props}
      actions={actions}
      title={title}
      className="bg-white"
    />
  );
};

export const MemoedHeaderRenderer = React.memo(
  BaseHeaderRenderer,
  (prev, next) => {
    // If both have cacheKey, compare them
    if (prev.cacheKey !== null && next.cacheKey !== null) {
      if (prev.cacheKey === next.cacheKey) {
        return prev.column.key === next.column.key;
      }
    }

    // If neither has cacheKey, fall back to React's default behavior
    if (prev.cacheKey === null && next.cacheKey === null) {
      return false; // Let React do its default shallow comparison
    }

    // If one has cacheKey and the other doesn't, they're different
    return false;
  }
);
